import { useCallback, useEffect, useRef, useState } from "react";
import approvalsService from "../api/services/approvalsService";
import { getSupportSocket } from "../api/services/supportSocket";
import { subscribeApprovalsPendingChanged } from "../utils/approvalsEvents";

export default function usePendingApprovalsCount(enabled = true) {
  const [count, setCount] = useState(0);
  const refreshTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const total = await approvalsService.getPendingCount();
      setCount(total);
    } catch {
      // Keep the last known count on transient failures.
    }
  }, [enabled]);

  const scheduleRefresh = useCallback(
    (delayMs = 400) => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = window.setTimeout(() => {
        refreshTimerRef.current = null;
        refresh();
      }, delayMs);
    },
    [refresh]
  );

  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }

    refresh();

    const onChanged = (detail = {}) => {
      if (typeof detail.delta === "number") {
        setCount((prev) => Math.max(0, prev + detail.delta));
        // Optimistic delta already applied — soft refresh to reconcile, don't hammer immediately
        scheduleRefresh(800);
        return;
      }
      scheduleRefresh(300);
    };

    const unsubscribe = subscribeApprovalsPendingChanged(onChanged);

    const socket = getSupportSocket();
    const onSocket = () => scheduleRefresh(300);
    socket.on("approvalsPendingChanged", onSocket);

    const interval = window.setInterval(refresh, 30000);

    return () => {
      unsubscribe();
      socket.off("approvalsPendingChanged", onSocket);
      window.clearInterval(interval);
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
    };
  }, [enabled, refresh, scheduleRefresh]);

  return count;
}
